import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API from "../config/api.js";

export default function ImageCard({
  image,
  showCaption = false,
  useCaption = false,
  useAsCarousel = true,
  showDate = false,
  hideSuppliers = false,
  isMyMedia = false,
  onRemove,
  onNoteSaved,
}) {
  const [isFavorite, setIsFavorite] = useState(image.idimagefavorite !== null);
  const [isUsed, setIsUsed] = useState(image.idimageused !== null);
  const [note, setNote] = useState(image.note || "");
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteDraft, setNoteDraft] = useState(image.note || "");
  const [savingNote, setSavingNote] = useState(false);
  const [isRotating, setIsRotating] = useState(false);

  const idImage = image.idimage;
  const isUserOwner = !!isMyMedia;

  const getImageUrl = (path, size = "350x350.jpg") => {
    if (!path) return "/assets/img/350x350.png";
    const url = `${API.baseUrl}/${path.replace("original.jpg", size)}`;
    return url;
  };

  const imagePreview = getImageUrl(image.image, "350x350.jpg");
  const transparentPlaceholder = "/assets/img/864x1080-transparent.png";
  const watermarkSrc = `/assets/img/${API.platform}/favicon.png`;

  const isNew =
    image.date &&
    new Date(image.date) > new Date(Date.now() - 40 * 24 * 60 * 60 * 1000);

  const categories = useMemo(() => {
    if (!image.categories) return [];
    if (Array.isArray(image.categories)) return image.categories;

    try {
      return JSON.parse(image.categories);
    } catch {
      return [];
    }
  }, [image.categories]);

  const suppliers = useMemo(() => {
    if (!image.suppliers) return [];
    if (Array.isArray(image.suppliers)) return image.suppliers;

    try {
      return JSON.parse(image.suppliers);
    } catch {
      return [];
    }
  }, [image.suppliers]);

  const handleCardClick = (e) => {
    e.preventDefault();

    if (
      e.target.closest(".action-buttons") ||
      e.target.closest(".image_carousel_checkbox") ||
      e.target.closest(".supplier-link")
    ) {
      return;
    }

    window._draft_prompt_image_add?.(
      idImage,
      showCaption || useCaption ? true : undefined,
      useAsCarousel,
    );
  };

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();

    window._draft_prompt_item_add?.(
      "image",
      idImage,
      image.image,
      showCaption || useCaption ? true : undefined,
    );
  };

  const handleAssistant = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window._assistant_image_add?.(idImage);
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(image.caption || "");
    } catch (err) {
      console.error("Erro ao copiar legenda:", err);
    }
  };

  const handleUsed = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const next = !isUsed;
    setIsUsed(next);

    try {
      const formData = new FormData();
      formData.append("idimage", idImage);

      const res = await fetch(`${API.images}?used`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const status = await res.json();
      setIsUsed(status === true);
    } catch (err) {
      setIsUsed(!next);
      console.error("Erro ao marcar imagem como usada:", err);
    }
  };

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const next = !isFavorite;
    setIsFavorite(next);

    try {
      const formData = new FormData();
      formData.append("idimage", idImage);

      const res = await fetch(`${API.images}?favorite`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const status = await res.json();
      setIsFavorite(status === true);
    } catch (err) {
      setIsFavorite(!next);
      console.error("Erro ao favoritar imagem:", err);
    }
  };

  const handleCarouselCheck = (e) => {
    e.stopPropagation();
    if (e.target.checked) {
      window._draft_prompt_image_carousel_pre_selected_check?.();
    } else {
      window._draft_prompt_image_carousel_pre_selected_remove?.(idImage);
    }
  };

  const handleRotate = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setIsRotating(true);

      const formData = new FormData();
      formData.append("idimage", idImage);

      const res = await fetch(`${API.drafts}?rotateImage90`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!res.ok) {
        throw new Error("Erro ao rotacionar imagem");
      }

      window._alert?.("success", "image rotated");
    } catch (err) {
      console.error("Erro ao rotacionar imagem:", err);
    } finally {
      setIsRotating(false);
      window.document.body.classList.remove("rotating-image");
    }
  };

  const handleRemove = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (window.confirm && !window.confirm("Remove this image?")) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("idimage", idImage);

      const res = await fetch(`${API.drafts}?removeImage`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!res.ok) {
        throw new Error("Erro ao remover imagem");
      }

      window._alert?.("success", "Image removed");
      window._draft_prompt_image_carousel_pre_selected_clear?.();

      onRemove?.(idImage);
    } catch (err) {
      console.error("Erro ao remover imagem:", err);
      window._alert?.("danger", "Error: image not removed");
    }
  };

  const handleOpenNote = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setNoteDraft(note || "");
    setShowNoteModal(true);
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();

    try {
      setSavingNote(true);

      const formData = new FormData();
      formData.append("idimage", idImage);
      formData.append("note", noteDraft);

      const res = await fetch(`${API.drafts}?addNote`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const result = await res.text();
      const success = result === "true" || result === "1";

      if (success) {
        setNote(noteDraft);
        setShowNoteModal(false);
        onNoteSaved?.(idImage, noteDraft);
        window._alert?.("success", "Note added");
      } else {
        window._alert?.("danger", "Error: note not added");
      }
    } catch (err) {
      console.error("Erro ao salvar nota:", err);
      window._alert?.("danger", "Error: note not added");
    } finally {
      setSavingNote(false);
    }
  };

const handleDownload = (e) => {
  e.preventDefault();
  e.stopPropagation();

  const a = document.createElement("a");
  a.href = `${API.images}?download&idimage=${idImage}`;
  a.download = `social-jet-ai-${idImage}.jpg`;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

  return (
    <>
      <div
        className={`image card border-0 image-${idImage} ${
          showDate ? "mb-4" : ""
        } animate__animated animate__fadeIn animate__slow`}
        data-idimage={idImage}
      >
        <div className="position-relative">
          {suppliers.length > 0 && !hideSuppliers && (
            <span className="position-absolute bottom-0 start-0 end-0 text-start p-3 z-3">
              {suppliers.map((supplier) => (
                <Link
                  key={supplier.idsupplier}
                  to={`/supplier?idsupplier=${supplier.idsupplier}`}
                  className="btn btn-sm mt-1 me-1 btn-white shadow-sm supplier-link"
                >
                  <i className="fi fi-rr-id-badge"></i> {supplier.name}
                </Link>
              ))}
            </span>
          )}

          <a
            href="/"
            onClick={handleCardClick}
            className="text-decoration-none text-dark"
          >
            <img
              src={transparentPlaceholder}
              style={{
                background: `url(${imagePreview}) center`,
                backgroundSize: "cover",
              }}
              alt=""
              className="img-fluid w-100 rounded-3"
              width="350"
              height="350"
            />

            <div className="position-absolute top-0 end-0 m-3 z-3">
              {isNew && (
                <div className="bg-white text-dark rounded shadow-sm py-1 text-center px-2 small d-inline-block new-tag">
                  <span className="mx-1">NEW</span>
                </div>
              )}
            </div>

            <div className="position-absolute m-2 mb-0 top-0 start-0 bottom-0 end-0 d-flex justify-content-center align-items-center text-center image-watermark">
              <img src={watermarkSrc} className="w-50" alt="watermark" />
            </div>
          </a>

          {!showCaption && (
            <span className="position-absolute top-0 end-0 text-start p-3 z-3">
              <input
                className="form-check-input image_carousel_checkbox mt-0"
                type="checkbox"
                value={idImage}
                onChange={handleCarouselCheck}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Multiple image selection"
              />
            </span>
          )}

          <div className="position-absolute top-0 start-0 m-1 card-actions z-3">
            <div className="mt-2 px-2 py-1 action-buttons">
              <button
                type="button"
                className="btn text-white"
                onClick={handleAdd}
              >
                <img
                  src="/assets/img/circle-plus.png"
                  width="24"
                  height="24"
                  alt="add"
                />
              </button>

              <div className="dropup dropend d-inline-block z-3 position-relative">
                <button
                  className="btn text-white"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img
                    src="/assets/img/dots.png"
                    width="24"
                    height="24"
                    alt="menu"
                  />
                </button>

                <ul className="dropdown-menu dropdown-menu-end rounded-3">
                  <li>
                    <button
                      className="dropdown-item btn-image-add-to-estelle"
                      onClick={handleAssistant}
                    >
                      <i className="fi fi-rr-bulb me-2"></i> Open in Estelle
                    </button>
                  </li>

                  {!showDate && showCaption && (
                    <li>
                      <button
                        className="dropdown-item btn-caption-copy"
                        onClick={handleCopy}
                      >
                        <i className="fi fi-rr-duplicate me-2"></i> Copy to clipboard
                      </button>
                    </li>
                  )}

                  <li>
                    <button
                      type="button"
                      className="dropdown-item btn-image-download"
                      onClick={handleDownload}
                    >
                      <i className="fi fi-rr-download me-2"></i> Download
                    </button>
                  </li>

                  <li>
                    <button
                      className="dropdown-item btn-image-used"
                      onClick={handleUsed}
                    >
                      {isUsed ? (
                        <>
                          <i className="fi fi-rr-checkbox me-2"></i> Mark As
                          Used
                        </>
                      ) : (
                        <>
                          <i className="fi fi-rr-square me-2"></i> Mark As Used
                        </>
                      )}
                    </button>
                  </li>

                  <li>
                    <button
                      className="dropdown-item btn-image-favorite"
                      onClick={handleFavorite}
                    >
                      {isFavorite ? (
                        <>
                          <i className="fi fi-sr-heart me-2"></i> Favorite
                        </>
                      ) : (
                        <>
                          <i className="fi fi-rr-heart me-2"></i> Favorite
                        </>
                      )}
                    </button>
                  </li>

                  {isUserOwner && (
                    <>
                      <li>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={handleRotate}
                          disabled={isRotating}
                        >
                          <i className="fi fi-rr-rotate-right me-2"></i>
                          {isRotating ? "Rotating..." : "Rotate"}
                        </button>
                      </li>

                      <li>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={handleOpenNote}
                        >
                          <i className="fi fi-rr-comment-alt me-2"></i> Add Note
                        </button>
                      </li>

                      <li>
                        <button
                          type="button"
                          className="dropdown-item text-danger"
                          onClick={handleRemove}
                        >
                          <i className="fi fi-rr-trash me-2"></i> Remove
                        </button>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div className="position-absolute bottom-0 start-0 end-0 p-2 m-1 z-3">
            {showCaption && (
              <>
                <h6
                  className="text-white"
                  style={{ fontWeight: 700, fontSize: "12px" }}
                >
                  {categories
                    .filter(
                      (category) =>
                        category.type === "caption" ||
                        category.type === "country",
                    )
                    .map((category) => (
                      <Link
                        key={`${idImage}-${category.idcategory}`}
                        className="text-white me-2 mb-2 text-decoration-none"
                        style={{ fontWeight: 700, fontSize: "12px" }}
                        to={`/captions?idcategory=${category.idcategory}`}
                      >
                        {category.title}
                      </Link>
                    ))}
                  &nbsp;
                </h6>

                <p
                  className="caption-content text-white"
                  dangerouslySetInnerHTML={{
                    __html: String(image.caption || "").replace(/\n/g, "<br>"),
                  }}
                />
              </>
            )}

            <div className="note text-white">{note}</div>
          </div>
        </div>
      </div>

      {showNoteModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <form className="modal-content" onSubmit={handleSaveNote}>
                <div className="modal-header">
                  <h5 className="modal-title">Add Note</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowNoteModal(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="image-preview mb-3">
                    <img
                      src={imagePreview}
                      className="img-fluid rounded-3"
                      alt=""
                    />
                  </div>

                  <textarea
                    name="note"
                    className="form-control"
                    rows="4"
                    placeholder="Write a note..."
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                  ></textarea>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setShowNoteModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={savingNote}
                  >
                    {savingNote ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
}
